import { isStrengthLikeWorkout } from "../../lib/coaching";
import React, { useState, useEffect } from "react";
import { X, Camera, Trash2, ChevronDown, ChevronUp, FileText, History, Repeat2, Utensils } from "lucide-react";
import {
  athleteLogWorkout,
  athleteListWorkoutComments,
  athletePostWorkoutComment,
  athleteGetNutritionHistory,
} from "../../lib/api";
import { parseDurationToMinutes, formatMinutesAsHhMm } from "../../lib/duration";
import StrengthExerciseLogger from "./StrengthExerciseLogger";
import GearLogSection from "./GearLogSection";
import WorkoutDetail from "./WorkoutDetail";
import WorkoutCommentsThread from "../WorkoutCommentsThread";
import { toast } from "sonner";

const STATUSES = [
  { value: "complete", label: "Complete", color: "#2F855A" },
  { value: "partial", label: "Partial", color: "#D69E2E" },
  { value: "skipped", label: "Skipped", color: "#7A1F2B" },
];

export default function AthleteWorkoutLogModal({ workout, programId, weekNumber, dayLabel, existingLog, onClose, onSaved }) {
  const [status, setStatus] = useState(existingLog?.status || "complete");
  const [distanceKm, setDistanceKm] = useState(existingLog?.distance_actual_km ?? "");
  const [durationMin, setDurationMin] = useState(
    existingLog?.duration_actual_min != null ? String(existingLog.duration_actual_min) : "",
  );
  // Free-text parse of the duration input. Athletes type in whatever
  // format they're used to ("90", "1:30", "1h30", "1.5 hrs") — we parse
  // to minutes here and use the result for both validation + submission.
  const parsedDurationMin = parseDurationToMinutes(durationMin);
  const [rpe, setRpe] = useState(existingLog?.rpe_actual ?? "");
  const [feeling, setFeeling] = useState(existingLog?.feeling || "");
  const [note, setNote] = useState(existingLog?.athlete_note || "");
  // Intra-workout nutrition — all optional; blank stays blank.
  const nut = existingLog?.nutrition || {};
  const [fuelItems, setFuelItems] = useState(nut.items || "");
  const [calHr, setCalHr] = useState(nut.calories_per_hour ?? "");
  const [carbsHr, setCarbsHr] = useState(nut.carbs_per_hour ?? "");
  const [fluids, setFluids] = useState(nut.fluids_ml ?? "");
  const g0 = existingLog?.gear || {};
  const [gear, setGear] = useState({ temperature: g0.temperature || "", shoes: g0.shoes || "", layers: g0.layers || "", note: g0.note || "" });
  const [nutHistOpen, setNutHistOpen] = useState(false);
  const [nutHist, setNutHist] = useState(null);
  const MAX_PHOTOS = 10;
  // Existing photos already stored (URLs) + newly picked (data URLs).
  const [keptUrls, setKeptUrls] = useState(
    () => existingLog?.photo_urls?.length ? existingLog.photo_urls : existingLog?.photo_url ? [existingLog.photo_url] : []
  );
  const [newPhotos, setNewPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  // Whether the "Workout details" panel inside the modal is expanded.
  // Default collapsed so the modal stays compact for a quick log entry; the
  // athlete taps the button when they need to re-read the description or
  // hit a link (per Natalie's request — there was no description visible
  // anywhere during logging).
  const [detailsOpen, setDetailsOpen] = useState(false);
  // Does this workout actually have anything useful to show in the details
  // panel? Hide the toggle entirely for barebones easy runs that only have
  // title + duration_or_distance — no point teasing an empty panel.
  const hasDetails =
    !!workout &&
    (
      (workout.segments && workout.segments.length > 0) ||
      (workout.strength_blocks && workout.strength_blocks.length > 0) ||
      (workout.session_note && workout.session_note.trim()) ||
      (workout.structure && workout.structure.trim()) ||
      workout.external_link ||
      workout.audio_link ||
      (workout.zones && workout.zones.length > 0) ||
      workout.rpe_min != null ||
      workout.rpe_max != null ||
      workout.add_strides
    );

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);

  const handlePhoto = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const room = MAX_PHOTOS - keptUrls.length - newPhotos.length;
    if (room <= 0) {
      toast.error(`Max ${MAX_PHOTOS} photos per workout`);
      return;
    }
    if (files.length > room) toast.error(`Only ${room} more photo${room === 1 ? "" : "s"} allowed (max ${MAX_PHOTOS})`);
    files.slice(0, room).forEach((file) => {
      if (file.size > 4 * 1024 * 1024) {
        toast.error(`${file.name} must be under 4 MB`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => setNewPhotos((prev) => (prev.length + keptUrls.length < MAX_PHOTOS ? [...prev, reader.result] : prev));
      reader.readAsDataURL(file);
    });
  };
  const totalPhotos = keptUrls.length + newPhotos.length;

  // Distance + Duration are REQUIRED on cardio workouts (run/walk/hike/cross
  // and any other run subtype) when the athlete marks complete/partial.
  // The use case: we need both for weekly mileage AND weekly cardio-time
  // rollups (the second is critical for ultra athletes whose volume is
  // measured in hours, not km). Strength/mobility/other don't count toward
  // either rollup so the fields stay optional there. Once Strava/Garmin
  // auto-matching is live, those will fill these and this becomes a no-op
  // for connected athletes.
  const isStrengthLike = isStrengthLikeWorkout(workout);
  const isRunningWorkout = !isStrengthLike && workout?.type !== "rest";
  const distanceRequired =
    isRunningWorkout && (status === "complete" || status === "partial");
  const durationRequired = distanceRequired;
  const distanceMissing =
    distanceRequired && (distanceKm === "" || distanceKm === null || Number.isNaN(parseFloat(distanceKm)));
  const durationMissing =
    durationRequired && (durationMin === "" || durationMin === null || parsedDurationMin == null);

  const submit = async () => {
    if (distanceMissing && durationMissing) {
      toast.error("Add the distance and time so this workout counts toward your weekly volume");
      return;
    }
    if (distanceMissing) {
      toast.error("Add the distance you ran so it counts toward your weekly mileage");
      return;
    }
    if (durationMissing) {
      toast.error("Add the time it took so it counts toward your weekly cardio volume");
      return;
    }
    setBusy(true);
    try {
      // For strength / mobility / other: distance and duration are hidden
      // from the form so we explicitly null them out in case an old log had
      // values from a previous workout type.
      const isNonRunning = isStrengthLike;
      const body = {
        program_id: programId,
        week_number: weekNumber,
        day_label: dayLabel,
        workout_id: workout.id,
        status,
        distance_actual_km:
          isNonRunning || distanceKm === "" ? null : parseFloat(distanceKm),
        duration_actual_min:
          isNonRunning || durationMin === "" || parsedDurationMin == null
            ? null
            : parsedDurationMin,
        rpe_actual: rpe === "" ? null : parseInt(rpe, 10),
        feeling: feeling || null,
        athlete_note: note || null,
        photo_base64s: newPhotos,
        photo_urls_keep: keptUrls,
        nutrition: {
          items: fuelItems.trim() || null,
          calories_per_hour: calHr === "" ? null : parseFloat(calHr),
          carbs_per_hour: carbsHr === "" ? null : parseFloat(carbsHr),
          fluids_ml: fluids === "" ? null : parseFloat(fluids),
        },
        gear: isRunningWorkout ? gear : null,
      };
      const saved = await athleteLogWorkout(body);
      toast.success("Logged");
      onSaved(saved);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't save log";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="athlete-log-modal"
    >
      <div
        className="bg-white border border-[var(--tir-ink)] w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-[var(--tir-border)] flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.24em] uppercase text-[var(--tir-muted)]">Log workout</div>
            <div className="font-serif-display text-xl mt-0.5">{workout.title || workout.type}</div>
            {workout.duration_or_distance && (
              <div className="text-[11px] text-[var(--tir-muted)] mt-0.5">
                Prescribed: {workout.duration_or_distance}
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-1 text-[var(--tir-muted)] hover:text-[var(--tir-ink)]">
            <X size={18} />
          </button>
        </div>

        {hasDetails && (
          <div className="border-b border-[var(--tir-border)] bg-[var(--tir-ivory)]/60">
            <button
              type="button"
              onClick={() => setDetailsOpen((v) => !v)}
              data-testid="workout-details-toggle"
              className="w-full px-5 py-2.5 flex items-center justify-between gap-2 text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-navy)] hover:bg-[var(--tir-ivory)] transition-colors"
            >
              <span className="inline-flex items-center gap-2">
                <FileText size={12} />
                Workout details
                <span className="text-[9px] normal-case tracking-normal text-[var(--tir-muted)] italic font-normal">
                  {detailsOpen ? "tap to hide" : "tap to read full plan + links"}
                </span>
              </span>
              {detailsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {detailsOpen && (
              <div
                className="px-5 pb-4 pt-1 bg-white border-t border-[var(--tir-border)]"
                data-testid="workout-details-panel"
              >
                <WorkoutDetail workout={workout} />
              </div>
            )}
          </div>
        )}

        <div className="p-5 space-y-5">
          {/* Status */}
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-2">
              Status
            </label>
            <div className="grid grid-cols-3 gap-2">
              {STATUSES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setStatus(s.value)}
                  data-testid={`status-${s.value}`}
                  className={`py-2.5 text-xs tracking-[0.18em] uppercase font-semibold border transition-colors ${
                    status === s.value
                      ? "text-white border-transparent"
                      : "bg-white border-[var(--tir-border)] text-[var(--tir-ink)] hover:border-[var(--tir-ink)]"
                  }`}
                  style={status === s.value ? { backgroundColor: s.color } : undefined}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {status !== "skipped" && (
            <>
              {/* Distance + duration are only meaningful for time/distance
                  workouts. Strength, mobility, and "other" are tracked by
                  status + RPE + note instead. */}
              {!isStrengthLike && (
                <div className="grid grid-cols-2 gap-3" data-testid="log-distance-duration-fields">
                  <div>
                    <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
                      Distance (km){distanceRequired && (
                        <span
                          className="ml-1 text-[var(--tir-burgundy)]"
                          aria-label="required"
                          title="Required when you mark this run complete or partial"
                        >
                          *
                        </span>
                      )}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={distanceKm}
                      onChange={(e) => setDistanceKm(e.target.value)}
                      data-testid="log-distance"
                      className={`w-full bg-white border focus:border-[var(--tir-navy)] px-3 py-2 text-sm focus:outline-none font-mono-tir ${
                        distanceMissing
                          ? "border-[var(--tir-burgundy)] bg-[var(--tir-burgundy)]/5"
                          : "border-[var(--tir-border)]"
                      }`}
                      placeholder={distanceRequired ? "e.g. 10.2" : ""}
                      required={distanceRequired}
                    />
                    {distanceMissing && (
                      <div
                        className="mt-1 text-[10px] text-[var(--tir-burgundy)] italic"
                        data-testid="log-distance-required-hint"
                      >
                        Add the distance so this run counts toward your weekly mileage.
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
                      Duration{durationRequired && (
                        <span
                          className="ml-1 text-[var(--tir-burgundy)]"
                          aria-label="required"
                          title="Required when you mark this workout complete or partial"
                        >
                          *
                        </span>
                      )}
                    </label>
                    <input
                      type="text"
                      inputMode="text"
                      value={durationMin}
                      onChange={(e) => setDurationMin(e.target.value)}
                      data-testid="log-duration"
                      className={`w-full bg-white border focus:border-[var(--tir-navy)] px-3 py-2 text-sm focus:outline-none font-mono-tir ${
                        durationMissing
                          ? "border-[var(--tir-burgundy)] bg-[var(--tir-burgundy)]/5"
                          : "border-[var(--tir-border)]"
                      }`}
                      placeholder={durationRequired ? "e.g. 45m, 1:30, 1h30, 1.5h" : "e.g. 45m, 1:30, 1h30"}
                      required={durationRequired}
                    />
                    {/* Live-parsed feedback so athletes can see how their
                        free-text will be stored. Only shown when they've
                        typed something valid — no noise on empty state. */}
                    {durationMin && parsedDurationMin != null && (
                      <div
                        className="mt-1 text-[10px] text-[var(--tir-muted)] italic"
                        data-testid="log-duration-parsed"
                      >
                        = {formatMinutesAsHhMm(parsedDurationMin)}
                        {parsedDurationMin >= 60 && (
                          <span> ({Math.round(parsedDurationMin)} min)</span>
                        )}
                      </div>
                    )}
                    {durationMissing && (
                      <div
                        className="mt-1 text-[10px] text-[var(--tir-burgundy)] italic"
                        data-testid="log-duration-required-hint"
                      >
                        Add the time so this counts toward your weekly cardio volume.
                      </div>
                    )}
                  </div>
                </div>
              )}
              {!isStrengthLike && (
                <div>
                  <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
                    RPE (1-10) — how it felt
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={rpe}
                    onChange={(e) => setRpe(e.target.value)}
                    data-testid="log-rpe"
                    className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-navy)] px-3 py-2 text-sm focus:outline-none font-mono-tir"
                  />
                </div>
              )}
              <FeelingGauge value={feeling} onChange={setFeeling} />
            </>
          )}

          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
              Note for your coach
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="How did it go? Anything to flag?"
              data-testid="log-note"
              className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-navy)] px-3 py-2 text-sm focus:outline-none leading-relaxed resize-y"
            />
          </div>

          {!isStrengthLike && (
          <div data-testid="nutrition-section">
            <div className="flex items-center justify-between mb-1">
              <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)]">
                <Utensils size={10} className="inline mr-1 -mt-0.5" />
                Intra-workout nutrition (optional)
              </label>
              <button
                type="button"
                onClick={async () => {
                  const next = !nutHistOpen;
                  setNutHistOpen(next);
                  if (next && nutHist === null) {
                    try {
                      setNutHist(await athleteGetNutritionHistory());
                    } catch {
                      setNutHist([]);
                    }
                  }
                }}
                data-testid="nutrition-history-btn"
                className="inline-flex items-center gap-1 text-[9px] tracking-[0.2em] uppercase font-semibold text-[var(--tir-navy)] hover:text-[var(--tir-ink)]"
              >
                <History size={11} /> History
              </button>
            </div>
            {nutHistOpen && (
              <div
                className="mb-2 border border-[var(--tir-border)] bg-[var(--tir-ivory)]/60 rounded-sm p-2 space-y-1.5 max-h-44 overflow-y-auto"
                data-testid="nutrition-history-panel"
              >
                {nutHist === null ? (
                  <div className="text-[11px] italic text-[var(--tir-muted)]">Loading…</div>
                ) : nutHist.length === 0 ? (
                  <div className="text-[11px] italic text-[var(--tir-muted)]">
                    No fuelling logged yet — this will fill up as you log.
                  </div>
                ) : (
                  nutHist.map((h, i) => {
                    const n = h.nutrition || {};
                    const when = h.updated_at || h.logged_at;
                    const parts = [
                      n.items,
                      n.calories_per_hour != null ? `${n.calories_per_hour} cal/hr` : null,
                      n.carbs_per_hour != null ? `${n.carbs_per_hour} g carbs/hr` : null,
                      n.fluids_ml != null ? `${n.fluids_ml} ml fluids` : null,
                    ].filter(Boolean);
                    return (
                      <div key={i} className="flex items-start gap-2" data-testid={`nutrition-history-row-${i}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-[9px] font-mono-tir text-[var(--tir-muted)]">
                            {when ? new Date(when).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}
                            {h.distance_actual_km ? ` · ${h.distance_actual_km} km` : ""}
                            {h.duration_actual_min ? ` · ${Math.round(h.duration_actual_min)} min` : ""}
                          </div>
                          <div className="text-[11px] text-[var(--tir-ink)] leading-snug">{parts.join(" · ")}</div>
                        </div>
                        <button
                          type="button"
                          title="Copy into this log"
                          data-testid={`nutrition-history-use-${i}`}
                          onClick={() => {
                            setFuelItems(n.items || "");
                            setCalHr(n.calories_per_hour ?? "");
                            setCarbsHr(n.carbs_per_hour ?? "");
                            setFluids(n.fluids_ml ?? "");
                            toast.success("Copied from last time — tweak as needed");
                          }}
                          className="shrink-0 text-[var(--tir-navy)] hover:text-[var(--tir-burgundy)] p-0.5"
                        >
                          <Repeat2 size={13} />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            )}
            <input
              value={fuelItems}
              onChange={(e) => setFuelItems(e.target.value)}
              placeholder="What did you have? e.g. 2 gels, 8 fuzzy peaches"
              data-testid="nutrition-items-input"
              className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-navy)] px-3 py-2 text-sm focus:outline-none mb-2"
            />
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-[9px] tracking-[0.18em] uppercase text-[var(--tir-muted)] mb-0.5">
                  Cal / hr
                </label>
                <input
                  type="number" min="0" inputMode="numeric"
                  value={calHr}
                  onChange={(e) => setCalHr(e.target.value)}
                  placeholder="e.g. 200"
                  data-testid="nutrition-cal-input"
                  className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-navy)] px-2 py-1.5 text-sm focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-[9px] tracking-[0.18em] uppercase text-[var(--tir-muted)] mb-0.5">
                  Carbs g / hr
                </label>
                <input
                  type="number" min="0" inputMode="numeric"
                  value={carbsHr}
                  onChange={(e) => setCarbsHr(e.target.value)}
                  placeholder="e.g. 60"
                  data-testid="nutrition-carbs-input"
                  className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-navy)] px-2 py-1.5 text-sm focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-[9px] tracking-[0.18em] uppercase text-[var(--tir-muted)] mb-0.5">
                  Fluids (ml)
                </label>
                <input
                  type="number" min="0" inputMode="numeric"
                  value={fluids}
                  onChange={(e) => setFluids(e.target.value)}
                  placeholder="e.g. 500"
                  data-testid="nutrition-fluids-input"
                  className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-navy)] px-2 py-1.5 text-sm focus:outline-none"
                />
              </div>
            </div>
          </div>
          )}

          {isRunningWorkout && <GearLogSection value={gear} onChange={setGear} />}

          {/* Per-exercise set logging — any workout built from strength
              blocks, whatever its Type dropdown says (e.g. "Lower Body B"
              saved as Easy run). */}
          {workout?.strength_blocks?.length > 0 && (
              <StrengthExerciseLogger
                workout={workout}
                programId={programId}
                weekNumber={weekNumber}
                dayLabel={dayLabel}
              />
            )}

          {/* Threaded conversation with the coach — visible whether or not
              there's a status logged. Coach replies surface in real time
              when the athlete reopens the modal, and the coach gets
              emailed when the athlete posts. */}
          <WorkoutCommentsThread
            side="athlete"
            programId={programId}
            weekNumber={weekNumber}
            dayLabel={dayLabel}
            workoutId={workout.id}
            listFn={athleteListWorkoutComments}
            postFn={athletePostWorkoutComment}
          />

          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
              Photos (optional) {totalPhotos > 0 && <span className="normal-case tracking-normal" data-testid="log-photo-count">· {totalPhotos}/{MAX_PHOTOS}</span>}
            </label>
            {totalPhotos > 0 && (
              <div className="mb-2 flex flex-wrap gap-2" data-testid="log-photo-grid">
                {keptUrls.map((u, i) => (
                  <div key={u} className="relative">
                    <img src={process.env.REACT_APP_BACKEND_URL + u} alt={`Photo ${i + 1}`} className="h-20 w-20 object-cover border border-[var(--tir-border)]" />
                    <button
                      type="button"
                      onClick={() => setKeptUrls((prev) => prev.filter((x) => x !== u))}
                      data-testid={`log-photo-remove-existing-${i}`}
                      className="absolute top-0.5 right-0.5 bg-white border border-[var(--tir-ink)] p-0.5"
                      aria-label="Remove photo"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))}
                {newPhotos.map((p, i) => (
                  <div key={`new-${i}`} className="relative">
                    <img src={p} alt={`New photo ${i + 1}`} className="h-20 w-20 object-cover border border-[var(--tir-burgundy)]" />
                    <button
                      type="button"
                      onClick={() => setNewPhotos((prev) => prev.filter((_, j) => j !== i))}
                      data-testid={`log-photo-remove-new-${i}`}
                      className="absolute top-0.5 right-0.5 bg-white border border-[var(--tir-ink)] p-0.5"
                      aria-label="Remove photo"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {totalPhotos < MAX_PHOTOS && (
              <label className="inline-flex items-center gap-2 px-3 py-2 border border-[var(--tir-border)] hover:border-[var(--tir-ink)] cursor-pointer text-xs tracking-[0.16em] uppercase">
                <Camera size={13} />
                {totalPhotos > 0 ? "Add more photos" : "Add photos"}
                <input type="file" accept="image/*" multiple onChange={handlePhoto} className="hidden" data-testid="log-photo-input" />
              </label>
            )}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-[var(--tir-border)] flex justify-end gap-2 bg-[var(--tir-ivory)]">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs tracking-[0.18em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-ink)]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || distanceMissing}
            data-testid="log-save-btn"
            title={distanceMissing ? "Add the distance to save this log" : ""}
            className="px-5 py-2 bg-[var(--tir-navy)] hover:bg-[var(--tir-navy)]/90 text-white text-xs tracking-[0.22em] uppercase font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? "Saving…" : "Save log"}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * 5-stop "How did you feel?" gauge — complements the numeric RPE with a
 * quick visual that captures overall sensation (not just effort). The
 * 1-10 RPE answers "how hard?" while this answers "how was it?".
 *
 * Values map onto a stable string enum so reports and trend graphs can
 * group across athletes regardless of language:
 *   very_weak | weak | normal | strong | very_strong
 *
 * Inline SVG smiley faces (no external icon library needed) — burgundy
 * stroke matches the TIR palette and inherits currentColor so selected
 * vs unselected state can swap with text colour utilities.
 */
const FEELINGS = [
  { value: "very_weak",   label: "Very weak",   render: (p) => <FaceVeryWeak {...p} /> },
  { value: "weak",        label: "Weak",        render: (p) => <FaceWeak {...p} /> },
  { value: "normal",      label: "Normal",      render: (p) => <FaceNormal {...p} /> },
  { value: "strong",      label: "Strong",      render: (p) => <FaceStrong {...p} /> },
  { value: "very_strong", label: "Very strong", render: (p) => <FaceVeryStrong {...p} /> },
];

function FeelingGauge({ value, onChange }) {
  return (
    <div data-testid="feeling-gauge">
      <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-2">
        How did you feel?
      </label>
      <div className="flex items-start justify-between gap-2">
        {FEELINGS.map((f) => {
          const selected = value === f.value;
          return (
            <button
              key={f.value}
              type="button"
              onClick={() => onChange(selected ? "" : f.value)}
              data-testid={`feeling-${f.value}`}
              aria-pressed={selected}
              aria-label={f.label}
              className={`flex-1 flex flex-col items-center gap-1.5 p-2 rounded-lg transition-all ${
                selected
                  ? "bg-[var(--tir-burgundy)]/10 ring-2 ring-[var(--tir-burgundy)]"
                  : "hover:bg-black/[0.03] ring-1 ring-transparent"
              }`}
            >
              <div
                className={`w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center transition-colors ${
                  selected
                    ? "bg-[var(--tir-burgundy)] text-white"
                    : "bg-black/[0.04] text-[var(--tir-muted)]"
                }`}
              >
                {f.render({ size: 26 })}
              </div>
              <span
                className={`text-[9px] tracking-[0.16em] uppercase font-semibold leading-tight text-center ${
                  selected ? "text-[var(--tir-burgundy)]" : "text-[var(--tir-muted)]"
                }`}
              >
                {f.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// --- Inline face SVGs (each 24x24, scaled via the `size` prop) -----------
const faceProps = { fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
function FaceVeryWeak({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...faceProps}>
      <circle cx="12" cy="12" r="10" />
      <path d="M8 9l2 2M10 9l-2 2" />
      <path d="M14 9l2 2M16 9l-2 2" />
      <path d="M8 17q4-4 8 0" />
    </svg>
  );
}
function FaceWeak({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...faceProps}>
      <circle cx="12" cy="12" r="10" />
      <circle cx="9" cy="10" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="15" cy="10" r="0.9" fill="currentColor" stroke="none" />
      <path d="M8 16q4-3 8 0" />
    </svg>
  );
}
function FaceNormal({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...faceProps}>
      <circle cx="12" cy="12" r="10" />
      <circle cx="9" cy="10" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="15" cy="10" r="0.9" fill="currentColor" stroke="none" />
      <path d="M8 15.5h8" />
    </svg>
  );
}
function FaceStrong({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...faceProps}>
      <circle cx="12" cy="12" r="10" />
      <circle cx="9" cy="10" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="15" cy="10" r="0.9" fill="currentColor" stroke="none" />
      <path d="M8 14.5q4 3 8 0" />
    </svg>
  );
}
function FaceVeryStrong({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...faceProps}>
      <circle cx="12" cy="12" r="10" />
      <path d="M7.5 9.5q1.5-1.5 3 0" />
      <path d="M13.5 9.5q1.5-1.5 3 0" />
      <path d="M7 14q5 5 10 0" />
    </svg>
  );
}
